import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  CircularProgress,
  Hidden,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';
import GetAppRounded from '@mui/icons-material/GetAppRounded';
import WarningAmber from '@mui/icons-material/WarningAmber';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { Events, Properties } from '../../enums/Analytics';
import { formatDateTime } from '../../lib/Date';
import { getSignableStatus, isVoidingRequired } from '../../enums/Signables';
import { ReactComponent as SignableIcon } from '../../svg/icons/signable.svg';
import { SignableProviders } from '../../enums/SignableProvider';
import { SignableSchema } from '../../definitions/schemas';
import { trackSignableEvent } from './analytics';
import { useShowConfirm } from '../../lib/hook/useShowConfirm';
import SignableMenu from './Menu/SignableMenu';
import SignablesInterface from '../../graphql/Signables/SignablesInterface';
import SignableStatus from './SignableStatus';
import SignableSync from './SignableSync';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import ThemableButton from '../../theme/components/Button/ThemableButton';
import useContentScroll from '../Room/Layout/useContentScroll';
import UserStore from '../../stores/observables/UserStore';
import { useI18n } from '../../lib/hook/useI18n';

const DATETIME_FORMAT = "MM/dd/yyyy 'at' hh:mm a";

const LazySigner = lazy(() => import('./Sign/SignableSigner'));

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    margin: 'auto',
    padding: theme.spacing(2),
    position: 'relative',
  },
  containerDotted: {
    border: '1px dashed var(--gray-light)',
    padding: theme.spacing(0.75, 2),
  },
  containerShadow: {
    boxShadow:
      '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)',
  },
  deleteButton: {
    alignItems: 'center',
    background: 'var(--gray-color)',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    height: theme.spacing(2.25),
    justifyContent: 'center',
    position: 'absolute',
    right: theme.spacing(-1.25),
    top: theme.spacing(-1),
    width: theme.spacing(2.25),
  },
  deleteIcon: {
    color: 'white',
    fontSize: theme.spacing(2),
    verticalAlign: 'baseline',
  },
  signableIcon: {
    color: theme.palette.secondary.main,
    height: theme.spacing(2.5),
    verticalAlign: 'top',
    width: theme.spacing(2.5),
  },
  signableIconContainer: {
    height: theme.spacing(2.5),
  },
}));

interface Props {
  allowMenu?: boolean;
  allowSync?: boolean;
  deleteable?: boolean;
  disableChangesOnScroll?: boolean;
  disableSign?: boolean;
  dottedBorder?: boolean;
  downloadSignableId?: string;
  isNew?: boolean;
  onDeleted?: (signable: SignableSchema) => void;
  onEdit?: () => void;
  onEditSigners?: () => void;
  onInhouseSignerToggled?: (open: boolean) => void;
  onPreview?: () => void;
  onReconnect?: () => void;
  onSynced?: (signable: SignableSchema, track?: boolean) => void;
  referrer: string;
  roomUuid?: string;
  scrollThresholdBase?: number;
  signable: SignableSchema;
  userContactId?: string;
}

export type PickedSignablesProps = Pick<
  Props,
  | 'disableChangesOnScroll'
  | 'disableSign'
  | 'downloadSignableId'
  | 'scrollThresholdBase'
>;

const Signable: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();
  const showConfirm = useShowConfirm();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('md'));

  const {
    allowMenu = false,
    allowSync = false,
    deleteable = false,
    disableChangesOnScroll = false,
    disableSign,
    dottedBorder = false,
    downloadSignableId,
    isNew,
    onDeleted,
    onEdit,
    onEditSigners,
    onInhouseSignerToggled,
    onPreview,
    onReconnect,
    onSynced,
    referrer,
    roomUuid,
    scrollThresholdBase = 0,
    signable,
    userContactId,
  } = props;

  const [, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiding, setVoiding] = useState(false);

  const [inhouseSignerOpen, setInhouseSignerOpen] = useState(false);

  const [hasStatusError, hasSyncStatusError, isCompleted] = useMemo(
    () => getSignableStatus(signable.status, signable.syncStatus),
    [signable]
  );

  const { current: isAuthenticatedUser } = useRef(!!UserStore.get());
  const { current: isInhouseSignable } = useRef(
    signable.provider === SignableProviders.EnableUs
  );

  const actualSignableDocument = useRef<SignableSchema | undefined>();
  const isContentScrolling = useContentScroll(scrollThresholdBase);
  const requiresVoidBeforeDeletion = isVoidingRequired(signable);

  const changeSizeOnScroll =
    signable.pendingSignature &&
    isContentScrolling &&
    !disableChangesOnScroll &&
    !smDown;

  useEffect(() => {
    // forcing a re-render with this flag, so the scroll trigger works
    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !downloadSignableId ||
      !isInhouseSignable ||
      !signable ||
      !isCompleted ||
      downloadSignableId !== signable.id
    ) {
      return;
    }

    window.open(signable.pdfUrl, '_blank');
  }, [downloadSignableId, isCompleted, isInhouseSignable, signable]);

  const loadInhouseSignable = async (): Promise<SignableSchema | undefined> => {
    try {
      setLoading(true);

      const response = await new SignablesInterface(userContactId).getSignable({
        id: signable.id,
      });

      setLoading(false);
      return response.getSignable.signable;
    } catch (e) {
      SnackbarStore.show(`${e}`);
      setLoading(false);
      return undefined;
    }
  };

  const loadDocusignSignableUrl = async (): Promise<string | undefined> => {
    if (!roomUuid) {
      return undefined;
    }

    try {
      setLoading(true);

      const response = await new SignablesInterface(
        userContactId
      ).getDocusignSignableUrl({
        redirectUrl: window.location.href,
        roomUuid,
        signableId: signable.id,
      });

      return response.url;
    } catch (e) {
      SnackbarStore.show(`${e}`);
      setLoading(false);
      return undefined;
    }
  };

  const handleEdit = () => {
    if (!onEdit) {
      return;
    }

    // if signable was not seen viewed, allow edit the signers/annotations
    if (signable.canBeEdited) {
      onEdit();
      return;
    }

    // this modal will void the existing signable and creates a new one from existing document
    showConfirm({
      confirmationText: 'Continue',
      maxWidth: 'xs',
      title: 'Before you edit',
      description: (
        <>
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--warning-color-dark)',
              display: 'flex',
              pb: 4,
            }}
          >
            <WarningAmber color="inherit" fontSize="small" />
            <Box component="span" ml={1}>
              <Typography color="inherit" component="span" variant="body2">
                Signable document was viewed by other parties
              </Typography>
            </Box>
          </Box>
          <Box component="span">
            <Typography color="inherit" component="span" variant="body2">
              After you save changes the previous version will be voided and all
              the signers will be informed via email
            </Typography>
          </Box>
        </>
      ),
    }).then(
      () => onEdit(),
      () => {}
    );
  };

  const handleDelete = () => {
    showConfirm({
      confirmationText: 'Delete',
      maxWidth: 'xs',
      title: 'Delete document',
      useErrorColor: true,
      description: (
        <>
          <Typography color="textSecondary" component="span">
            Are you sure you want to delete {signable.name}?
          </Typography>
          {requiresVoidBeforeDeletion && (
            <Box
              component="span"
              sx={{
                color: 'var(--warning-color-dark)',
                display: 'flex',
                mt: 1.5,
              }}
            >
              <WarningAmber color="inherit" fontSize="small" sx={{ mt: 0.5 }} />
              <Box component="span" ml={1}>
                <Typography color="inherit" component="span" variant="body2">
                  Deleting will void the document and all the signers will be
                  informed via email
                </Typography>
              </Box>
            </Box>
          )}
        </>
      ),
    }).then(
      () => onSignableDelete(),
      () => {}
    );
  };

  const voidSignableForDeletion = async () => {
    try {
      setVoiding(true);
      const reason =
        'Document has been deleted and was therefore automatically voided';
      const {
        voidSignable: { signable: updatedSignable },
      } = await new SignablesInterface().void({
        id: signable.id,
        reason,
        notifySigners: true,
      });

      setVoiding(false);
      trackSignableEvent(
        Events.SIGNABLE_DOCUMENT_VOIDED,
        referrer,
        updatedSignable,
        {
          [Properties.REASON]: reason,
        }
      );
      return updatedSignable;
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  const onSignableDelete = async () => {
    const deletedSignable = requiresVoidBeforeDeletion
      ? await voidSignableForDeletion()
      : signable;

    if (onDeleted && deletedSignable) {
      onDeleted(deletedSignable);
    }
  };

  const onInhouseSignerClose = () => {
    setInhouseSignerOpen(false);
    actualSignableDocument.current = undefined;

    if (onInhouseSignerToggled) {
      onInhouseSignerToggled(false);
    }
  };

  const onSignableUpdated = (updatedSignable: SignableSchema) => {
    onInhouseSignerClose();

    if (onSynced) {
      onSynced(updatedSignable, false);
    }
  };

  const handleDownload = () => {
    const event = isAuthenticatedUser
      ? Events.SIGNABLE_DOCUMENT_USER_DOWNLOADED
      : Events.SIGNABLE_DOCUMENT_CONTACT_DOWNLOADED;

    trackSignableEvent(event, referrer, signable);
  };

  const handleSign = () => {
    if (signable.provider === SignableProviders.EnableUs) {
      handleInhouseSign();
      return;
    }

    handleThirdPartySign();
  };

  const handleInhouseSign = async () => {
    const signableDocument = await loadInhouseSignable();
    if (!signableDocument) {
      return;
    }

    actualSignableDocument.current = signableDocument;
    trackViewEvent(signableDocument);
    setInhouseSignerOpen(true);

    // update signable after the user viewed the document, to reflect viewed event on tooltip
    if (onSynced) {
      onSynced(signableDocument, false);
    }

    if (onInhouseSignerToggled) {
      onInhouseSignerToggled(true);
    }
  };

  const handleThirdPartySign = async () => {
    const url = await loadDocusignSignableUrl();

    trackViewEvent(signable);

    // using a timeout to allow the analytics to finish the request before navigating out
    setTimeout(() => {
      if (url) {
        window.location.href = url;
      }
    }, 250);
  };

  const trackViewEvent = (signable: SignableSchema) => {
    const event = isAuthenticatedUser
      ? Events.SIGNABLE_DOCUMENT_USER_VIEWED
      : Events.SIGNABLE_DOCUMENT_CONTACT_VIEWED;

    trackSignableEvent(event, referrer, signable);
  };

  const renderStatus = (): JSX.Element | undefined => {
    if (!signable.signers) {
      return undefined;
    }

    return (
      <SignableStatus
        disabled={voiding}
        isInhouseSignable={isInhouseSignable}
        signers={signable.signers}
        status={signable.status}
        syncStatus={signable.syncStatus}
      />
    );
  };

  const renderSign = (): JSX.Element => (
    <Box ml={3}>
      <ThemableButton
        disabled={disableSign || loading}
        onClick={handleSign}
        size="small"
        startIcon={
          loading ? <CircularProgress color="inherit" size={16} /> : undefined
        }
        style={{ textTransform: 'none' }}
        variant="contained"
      >
        <span>{t('esignature.box.sign')}</span>
      </ThemableButton>
    </Box>
  );

  const renderDownload = (): JSX.Element => (
    <Box ml={3}>
      <ThemableButton
        component="a"
        href={signable.url}
        rel="noopener noreferrer"
        onClick={handleDownload}
        size="small"
        startIcon={<GetAppRounded />}
        style={{ textTransform: 'none' }}
        target="_blank"
        variant="contained"
      >
        {t('esignature.box.download')}
      </ThemableButton>
    </Box>
  );

  const renderReconnect = (): JSX.Element | undefined =>
    !onReconnect ? undefined : (
      <Box ml={3}>
        <ThemableButton
          onClick={onReconnect}
          size="small"
          style={{ textTransform: 'none' }}
          variant="contained"
        >
          Connect
        </ThemableButton>
      </Box>
    );

  const renderAction = (): JSX.Element | undefined => {
    if (hasStatusError) {
      return undefined;
    }

    if (hasSyncStatusError) {
      return renderReconnect();
    }

    if (isCompleted && signable.url) {
      return renderDownload();
    }

    if (signable.pendingSignature) {
      return renderSign();
    }

    return undefined;
  };

  const renderMenu = (): JSX.Element | undefined => {
    if (!allowMenu || !isInhouseSignable || hasSyncStatusError) {
      return undefined;
    }

    if (voiding) {
      return (
        <Box ml={1.5} p={1}>
          <CircularProgress color="secondary" size={20} />
        </Box>
      );
    }

    return (
      <Box ml={1}>
        <SignableMenu
          isNew={isNew}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onEditSigners={onEditSigners}
          onPreview={onPreview}
          onRenamed={onSignableUpdated}
          onVoided={onSignableUpdated}
          referrer={referrer}
          signable={signable}
        />
      </Box>
    );
  };

  const renderSync = (): JSX.Element | undefined => {
    if (isInhouseSignable || !(allowSync && signable.lastSyncAt)) {
      return undefined;
    }

    const formattedLastSyncAt = formatDateTime(
      signable.lastSyncAt,
      DATETIME_FORMAT
    );

    return (
      <Box alignItems="flex-start" display="flex" mt={0.5}>
        <Typography color="textSecondary" variant="caption">
          {`Last synced: ${formattedLastSyncAt}`}
        </Typography>
        {!hasSyncStatusError && (
          <Box mb={-1} ml={1}>
            <SignableSync onSynced={onSynced} signable={signable} />
          </Box>
        )}
      </Box>
    );
  };

  const renderDelete = (): JSX.Element | undefined =>
    !deleteable || isInhouseSignable ? undefined : (
      <Box className={classes.deleteButton}>
        <IconButton onClick={handleDelete} size="small">
          <Close className={classes.deleteIcon} fontSize="small" />
        </IconButton>
      </Box>
    );

  return (
    <>
      <Box
        className={classNames(classes.container, {
          [classes.containerDotted]: dottedBorder,
          [classes.containerShadow]: changeSizeOnScroll,
        })}
        width={changeSizeOnScroll ? '97%' : 'auto'}
      >
        <Box alignItems="center" display="flex">
          <Box alignItems="center" display="flex" flex="1" minWidth={0}>
            <Box className={classes.signableIconContainer} mr={1}>
              <SignableIcon className={classes.signableIcon} />
            </Box>
            <Typography
              noWrap
              sx={{ color: voiding ? 'text.disabled' : 'text.primary' }}
              title={signable.name}
              variant="body2"
            >
              <Box component="strong">{signable.name}</Box>
            </Typography>
          </Box>
          <Hidden mdDown>
            <Box ml={3}>{renderStatus()}</Box>
            {renderAction()}
            {renderMenu()}
          </Hidden>
        </Box>
        <Hidden mdUp>
          <Box alignItems="center" display="flex" mt={1}>
            <Box flex="1">{renderStatus()}</Box>
            <Box ml={3}>{renderAction()}</Box>
          </Box>
        </Hidden>
        {renderSync()}
        {renderDelete()}
      </Box>

      {isInhouseSignable && (
        <Suspense fallback={<></>}>
          <LazySigner
            onClose={onInhouseSignerClose}
            onDownload={handleDownload}
            onSigned={onSignableUpdated}
            open={inhouseSignerOpen}
            referrer={referrer}
            signable={actualSignableDocument.current}
            userContactId={userContactId}
          />
        </Suspense>
      )}
    </>
  );
};

export default Signable;
